import { Injectable } from '@angular/core';
import {
  isEqual,
  isMatch,
  isValid,
  parse,
  startOfDay,
  startOfWeek,
} from 'date-fns';

import { TimesheetUIPreferences } from '../models/ui-preferences';
import { weekEnd } from './time-tracker.utils';

export interface TimesheetWeekRange {
  start: Date;
  end: Date;
}

export interface StartingWeekOptions {
  queryWeek?: string;
  preference?: TimesheetUIPreferences;
  recentStatuses?: [string, string][];
  today?: Date;
}

@Injectable()
export class TimesheetWeekNavigationService {
  chooseStartingWeek(options: StartingWeekOptions): TimesheetWeekRange {
    const today = options.today ?? new Date();
    if (options.queryWeek) return this.fromWeekString(options.queryWeek, today);

    const strategy =
      options.preference?.defaultTimesheetWeek ?? 'oldest-unsubmitted';
    if (strategy === 'last-opened') {
      const lastWeekViewed = options.preference?.lastWeekViewed
        ? new Date(options.preference.lastWeekViewed)
        : undefined;
      if (lastWeekViewed && isValid(lastWeekViewed)) {
        return this.weekContaining(lastWeekViewed);
      }
    }

    if (strategy === 'oldest-unsubmitted') {
      const oldest = (options.recentStatuses ?? [])
        .filter(([, status]) => status !== 'Submitted' && status !== 'Approved')
        .map(([weekStart]) => weekStart)
        .sort()[0];
      if (oldest) return this.fromWeekString(oldest, today);
    }

    return this.weekContaining(today);
  }

  fromWeekString(week: string, fallback = new Date()): TimesheetWeekRange {
    const formats = ['yyyy-MM-dd', 'yyyy/MM/dd'];
    const parsed = parse(
      week,
      formats.find((dateFormat) => isMatch(week, dateFormat)) ?? formats[0],
      fallback,
    );
    return this.weekContaining(isValid(parsed) ? parsed : fallback);
  }

  weekContaining(day: Date): TimesheetWeekRange {
    const start = startOfWeek(day);
    return { start, end: weekEnd(start) };
  }

  offsetWeek(start: Date | null, offset: number, fallback = new Date()): TimesheetWeekRange {
    const nextStart = startOfDay(start ?? fallback);
    nextStart.setDate(nextStart.getDate() + offset * 7);
    return this.weekContaining(nextStart);
  }

  normalizeRange(start: Date, end: Date, fallback = new Date()): TimesheetWeekRange {
    const validStart = isValid(start) ? start : startOfWeek(fallback);
    const validEnd = isValid(end) ? end : weekEnd(validStart);
    return { start: validStart, end: validEnd };
  }

  isSameWeek(candidate: Date, selected: Date | null, fallback = new Date()): boolean {
    return isEqual(
      startOfWeek(candidate),
      startOfWeek(selected ?? fallback),
    );
  }
}
